import { useState, useEffect } from 'react';
import { Download } from 'lucide-react';

/**
 * "Instalar app" button. Captures the browser's beforeinstallprompt event so the
 * user can install the PWA on demand, instead of relying on Chrome's one-time
 * automatic banner (which is suppressed after installing/dismissing/uninstalling).
 * Renders nothing when already installed or when the browser can't install.
 */
export default function InstallButton({ className = '', label = 'Instalar app' }) {
  const [deferred, setDeferred] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    // Already running as an installed app → nothing to do.
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (standalone) { setHidden(true); return; }

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setHidden(true); setDeferred(null); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (hidden || !deferred) return null;

  const install = async () => {
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
  };

  return (
    <button
      onClick={install}
      className={className || 'flex items-center text-[#1a4f8b] font-bold text-sm bg-blue-50 px-3 py-1.5 rounded-full hover:bg-blue-100'}
    >
      <Download className="w-4 h-4 mr-1" /> {label}
    </button>
  );
}
